/**
 * AssignmentsPanel — dos modos en un solo panel:
 *   - `openForStudent()`: lista las tareas asignadas al alumno. Cada row
 *     tiene botón "Comenzar" que carga el caso (built-in o custom).
 *   - `openForTeacher()`: lista asignaciones del profesor + crear nuevas.
 *
 * El panel sigue la estética del CaseAdminPanel (modal oscuro, backdrop).
 */

import { authService } from '../auth/AuthService';
import state from '../core/state';
import { assignmentsService, type Assignment } from './AssignmentsService';

const PANEL_ID = 'cgt-assignments';
const STYLE_ID = 'cgt-assignments-styles';

const STATUS_META: Record<Assignment['status'], { label: string; color: string }> = {
  pending: { label: 'Pendiente', color: '#FF9F0A' },
  done: { label: 'Completada', color: '#30D158' },
  overdue: { label: 'Vencida', color: '#FF453A' },
};

const BUILTIN_CASES: Array<{ slug: string; label: string }> = [
  { slug: 'iam', label: 'IAM (Infarto agudo de miocardio)' },
  { slug: 'sepsis', label: 'Sepsis' },
  { slug: 'epoc', label: 'EPOC exacerbado' },
];

export class AssignmentsPanel {
  private root: HTMLElement | null = null;
  private mode: 'student' | 'teacher' = 'student';

  openForStudent(): void {
    if (!authService.isAuthenticated) return;
    this.mode = 'student';
    this.mount();
    void this.refresh();
  }

  openForTeacher(): void {
    if (authService.currentUser?.role !== 'teacher') return;
    this.mode = 'teacher';
    this.mount();
    void this.refresh();
  }

  close(): void {
    if (!this.root) return;
    const el = this.root;
    el.classList.remove('cgt-assign-visible');
    setTimeout(() => el.remove(), 200);
    this.root = null;
  }

  private mount(): void {
    if (this.root) return;
    this.injectStyles();
    const root = document.createElement('div');
    root.id = PANEL_ID;
    root.innerHTML = `
      <div class="cgt-assign-backdrop" data-close></div>
      <div class="cgt-assign-card">
        <header>
          <h2>${this.mode === 'teacher' ? 'Asignaciones del curso' : 'Mis tareas'}</h2>
          <button type="button" class="cgt-assign-close" data-close aria-label="Cerrar">×</button>
        </header>
        ${this.mode === 'teacher' ? this.renderTeacherHeader() : ''}
        <div class="cgt-assign-body" data-body>
          <div class="cgt-assign-empty">Cargando…</div>
        </div>
      </div>
    `;
    root.querySelectorAll<HTMLElement>('[data-close]').forEach(el =>
      el.addEventListener('click', () => this.close()),
    );
    if (this.mode === 'teacher') {
      root.querySelector<HTMLButtonElement>('[data-new-assignment]')
        ?.addEventListener('click', () => void this.promptNewAssignment());
    }
    document.body.appendChild(root);
    requestAnimationFrame(() => root.classList.add('cgt-assign-visible'));
    this.root = root;
  }

  private renderTeacherHeader(): string {
    return `
      <div class="cgt-assign-toolbar">
        <p>Asigná casos específicos a tus alumnos con fecha límite opcional.</p>
        <button type="button" class="cgt-assign-primary" data-new-assignment>＋ Nueva asignación</button>
      </div>
    `;
  }

  private async refresh(): Promise<void> {
    const body = this.root?.querySelector<HTMLElement>('[data-body]');
    if (!body) return;
    try {
      const rows = this.mode === 'teacher'
        ? await assignmentsService.listForTeacher()
        : await assignmentsService.listForMe();
      if (rows.length === 0) {
        body.innerHTML = `<div class="cgt-assign-empty">${
          this.mode === 'teacher'
            ? 'Aún no hay asignaciones. Usá "Nueva asignación" para empezar.'
            : 'No tenés tareas asignadas. ¡Podés explorar los casos libremente!'
        }</div>`;
        return;
      }
      body.innerHTML = rows.map(r => this.renderRow(r)).join('');
      this.attachRowHandlers(body);
    } catch (e) {
      body.innerHTML = `<div class="cgt-assign-error">Error: ${esc(e instanceof Error ? e.message : String(e))}</div>`;
    }
  }

  private renderRow(r: Assignment): string {
    const meta = STATUS_META[r.status];
    const due = r.due_at ? new Date(r.due_at).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' }) : '—';
    const caseLabel = BUILTIN_CASES.find(c => c.slug === r.case_slug)?.label ?? r.case_slug;
    const counterpart = this.mode === 'teacher'
      ? `<small>Alumno: ${esc(r.student_name ?? '?')}</small>`
      : `<small>Profesor: ${esc(r.student_name ?? '?')}</small>`;

    const actions = this.mode === 'teacher'
      ? `<button type="button" class="cgt-assign-del" data-del="${r.id}" title="Borrar">×</button>`
      : r.status === 'done'
        ? `<span class="cgt-assign-score">${r.last_score ?? '—'}/100</span>`
        : `<button type="button" class="cgt-assign-start" data-start="${esc(r.case_slug)}">Comenzar →</button>`;

    return `
      <article class="cgt-assign-row" data-id="${r.id}">
        <span class="cgt-assign-dot" style="background:${meta.color}" title="${esc(meta.label)}"></span>
        <div class="cgt-assign-info">
          <strong>${esc(caseLabel)}</strong>
          ${counterpart}
          <span class="cgt-assign-meta">
            <span style="color:${meta.color}">${esc(meta.label)}</span>
            · Vence: ${esc(due)}
            ${r.note ? ` · ${esc(r.note.slice(0, 60))}` : ''}
          </span>
        </div>
        ${actions}
      </article>
    `;
  }

  private attachRowHandlers(body: HTMLElement): void {
    // Teacher: delete
    body.querySelectorAll<HTMLButtonElement>('[data-del]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const id = Number(btn.dataset.del);
        if (!id || !confirm('¿Eliminar esta asignación?')) return;
        try {
          await assignmentsService.delete(id);
          await this.refresh();
        } catch (err) {
          alert(err instanceof Error ? err.message : String(err));
        }
      });
    });
    // Student: start (capturamos el id de la asignación para auto-mark-done)
    body.querySelectorAll<HTMLButtonElement>('[data-start]').forEach(btn => {
      btn.addEventListener('click', () => {
        const slug = btn.dataset.start!;
        const row = btn.closest<HTMLElement>('[data-id]');
        const id = row?.dataset.id ? Number(row.dataset.id) : null;
        this.startCase(slug, id);
      });
    });
  }

  private startCase(slug: string, assignmentId: number | null = null): void {
    // Guardamos el id para que al completar el caso el simulador llame
    // markDone automáticamente. Se seteia ANTES del loadCase para que
    // state.setPatient (invocado adentro) no lo pise.
    state.activeAssignmentId = assignmentId;
    this.close();
    const app = (window as unknown as {
      app?: {
        loadCase?: (n: 'iam' | 'sepsis' | 'epoc') => void;
        loadCustomCase?: (s: string) => void;
      };
    }).app;
    if (slug === 'iam' || slug === 'sepsis' || slug === 'epoc') {
      app?.loadCase?.(slug);
    } else {
      void app?.loadCustomCase?.(slug);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Modal "Nueva asignación" (teacher)
  // ─────────────────────────────────────────────────────────────
  private async promptNewAssignment(): Promise<void> {
    // Traer lista de alumnos desde /teacher/students
    type Student = { id: number; display_name: string; email: string };
    let students: Student[] = [];
    try {
      students = await authService.request<Student[]>('/teacher/students?order_by=xp');
    } catch {
      students = [];
    }
    if (students.length === 0) {
      alert('Todavía no hay alumnos registrados.');
      return;
    }

    const dialog = document.createElement('div');
    dialog.className = 'cgt-assign-dialog';
    const studentOpts = students.map(s =>
      `<label><input type="checkbox" value="${s.id}" /> ${esc(s.display_name)} <small>${esc(s.email)}</small></label>`
    ).join('');
    const caseOpts = BUILTIN_CASES.map(c =>
      `<option value="${esc(c.slug)}">${esc(c.label)}</option>`
    ).join('');

    dialog.innerHTML = `
      <div class="cgt-assign-dialog-card">
        <h3>Nueva asignación</h3>
        <div class="cgt-assign-field">
          <span>Caso</span>
          <select data-case>${caseOpts}</select>
          <small>Los casos creados por vos también pueden escribirse como slug manualmente.</small>
        </div>
        <div class="cgt-assign-field">
          <span>Slug custom (opcional)</span>
          <input type="text" data-slug placeholder="ej: dka-dm1 (sobreescribe el dropdown)" />
        </div>
        <div class="cgt-assign-field">
          <span>Fecha límite (opcional)</span>
          <input type="datetime-local" data-due />
        </div>
        <div class="cgt-assign-field">
          <span>Alumnos</span>
          <div class="cgt-assign-students">${studentOpts}</div>
        </div>
        <div class="cgt-assign-field">
          <span>Nota (opcional)</span>
          <textarea data-note rows="2" placeholder="Instrucciones breves para el alumno"></textarea>
        </div>
        <div class="cgt-assign-dialog-actions">
          <button type="button" class="cgt-assign-secondary" data-cancel>Cancelar</button>
          <button type="button" class="cgt-assign-primary" data-confirm>Asignar</button>
        </div>
      </div>
    `;
    document.body.appendChild(dialog);
    dialog.querySelector<HTMLButtonElement>('[data-cancel]')?.addEventListener('click', () => dialog.remove());
    dialog.querySelector<HTMLButtonElement>('[data-confirm]')?.addEventListener('click', async () => {
      const caseSel = dialog.querySelector<HTMLSelectElement>('[data-case]')!.value;
      const slugOverride = dialog.querySelector<HTMLInputElement>('[data-slug]')!.value.trim();
      const due = dialog.querySelector<HTMLInputElement>('[data-due]')!.value;
      const note = dialog.querySelector<HTMLTextAreaElement>('[data-note]')!.value.trim();
      const ids = Array.from(dialog.querySelectorAll<HTMLInputElement>('input[type=checkbox]:checked'))
        .map(el => Number(el.value));
      if (ids.length === 0) {
        alert('Seleccioná al menos un alumno.');
        return;
      }
      try {
        await assignmentsService.create({
          student_ids: ids,
          case_slug: slugOverride || caseSel,
          due_at: due ? new Date(due).toISOString() : undefined,
          note: note || undefined,
        });
        dialog.remove();
        await this.refresh();
      } catch (err) {
        alert(err instanceof Error ? err.message : String(err));
      }
    });
  }

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      #${PANEL_ID} {
        position: fixed; inset: 0; z-index: 14500;
        display: flex; align-items: center; justify-content: center;
        opacity: 0; transition: opacity 0.2s;
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
        color: #f5f5f7;
      }
      #${PANEL_ID}.cgt-assign-visible { opacity: 1; }
      #${PANEL_ID} .cgt-assign-backdrop {
        position: absolute; inset: 0;
        background: rgba(0,0,0,0.6); backdrop-filter: blur(6px);
      }
      #${PANEL_ID} .cgt-assign-card {
        position: relative; width: min(720px, 94vw); max-height: 88vh;
        background: #0a0a0a; border: 0.5px solid rgba(255,255,255,0.1);
        border-radius: 18px; overflow: hidden;
        display: flex; flex-direction: column;
        box-shadow: 0 24px 80px rgba(0,0,0,0.5);
      }
      #${PANEL_ID} header {
        display: flex; align-items: center; justify-content: space-between;
        padding: 18px 24px; border-bottom: 0.5px solid rgba(255,255,255,0.06);
      }
      #${PANEL_ID} header h2 { font-size: 18px; font-weight: 700; letter-spacing: -0.3px; }
      #${PANEL_ID} .cgt-assign-close {
        width: 28px; height: 28px; border-radius: 50%; border: none;
        background: rgba(255,255,255,0.06); color: #a1a1a6; cursor: pointer; font-size: 18px;
      }
      #${PANEL_ID} .cgt-assign-toolbar {
        padding: 12px 24px; display: flex; align-items: center; justify-content: space-between;
        gap: 12px; background: rgba(255,255,255,0.015);
        border-bottom: 0.5px solid rgba(255,255,255,0.04);
      }
      #${PANEL_ID} .cgt-assign-toolbar p { font-size: 12px; color: #a1a1a6; margin: 0; }
      #${PANEL_ID} .cgt-assign-body { overflow-y: auto; padding: 14px; flex: 1; }
      #${PANEL_ID} .cgt-assign-empty, #${PANEL_ID} .cgt-assign-error {
        padding: 40px 20px; text-align: center; font-size: 13px; color: #8e8e93;
      }
      #${PANEL_ID} .cgt-assign-error { color: #FF6961; }
      #${PANEL_ID} .cgt-assign-row {
        display: flex; align-items: center; gap: 12px;
        padding: 12px; margin-bottom: 8px;
        background: rgba(255,255,255,0.02);
        border: 0.5px solid rgba(255,255,255,0.06);
        border-radius: 10px;
      }
      #${PANEL_ID} .cgt-assign-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
      #${PANEL_ID} .cgt-assign-info { flex: 1; min-width: 0; }
      #${PANEL_ID} .cgt-assign-info strong {
        display: block; font-size: 13px; color: #f5f5f7;
      }
      #${PANEL_ID} .cgt-assign-info small {
        display: block; font-size: 11px; color: #6e6e73; margin-top: 2px;
      }
      #${PANEL_ID} .cgt-assign-meta { display: block; font-size: 10.5px; color: #8e8e93; margin-top: 4px; }
      #${PANEL_ID} .cgt-assign-start {
        background: linear-gradient(135deg, #2997FF, #BF5AF2); color: #fff;
        border: none; border-radius: 8px; padding: 8px 14px;
        cursor: pointer; font-weight: 600; font-size: 12px; font-family: inherit;
      }
      #${PANEL_ID} .cgt-assign-score {
        font-family: 'JetBrains Mono', monospace; font-weight: 700;
        color: #30D158; font-size: 13px;
      }
      #${PANEL_ID} .cgt-assign-del {
        width: 24px; height: 24px; border: none; border-radius: 50%;
        background: rgba(255,69,58,0.15); color: #FF6961; cursor: pointer; font-size: 13px;
      }
      #${PANEL_ID} .cgt-assign-primary {
        background: #2997FF; color: #fff; border: none; border-radius: 8px;
        padding: 8px 16px; cursor: pointer; font-weight: 600; font-size: 12px; font-family: inherit;
      }
      /* Diálogo "nueva asignación" */
      .cgt-assign-dialog {
        position: fixed; inset: 0; z-index: 15500;
        background: rgba(0,0,0,0.6); backdrop-filter: blur(8px);
        display: flex; align-items: center; justify-content: center;
      }
      .cgt-assign-dialog-card {
        background: #0a0a0a; border-radius: 16px; padding: 24px 28px;
        width: min(480px, 94vw); max-height: 90vh; overflow-y: auto;
        color: #f5f5f7; border: 0.5px solid rgba(255,255,255,0.1);
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
      }
      .cgt-assign-dialog-card h3 { font-size: 16px; font-weight: 700; margin-bottom: 16px; }
      .cgt-assign-dialog-card .cgt-assign-field {
        display: flex; flex-direction: column; gap: 4px; margin-bottom: 14px;
      }
      .cgt-assign-dialog-card .cgt-assign-field span {
        font-size: 10.5px; color: #8e8e93; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;
      }
      .cgt-assign-dialog-card .cgt-assign-field small {
        font-size: 10.5px; color: #6e6e73;
      }
      .cgt-assign-dialog-card input, .cgt-assign-dialog-card select, .cgt-assign-dialog-card textarea {
        background: #050505; color: #fff; border: 0.5px solid rgba(255,255,255,0.1);
        border-radius: 8px; padding: 8px 12px; font-size: 12.5px; font-family: inherit;
      }
      .cgt-assign-dialog-card input:focus, .cgt-assign-dialog-card select:focus, .cgt-assign-dialog-card textarea:focus {
        outline: none; border-color: #2997FF;
      }
      .cgt-assign-students {
        display: flex; flex-direction: column; gap: 6px;
        max-height: 200px; overflow-y: auto; padding: 8px;
        background: rgba(255,255,255,0.02); border-radius: 8px;
      }
      .cgt-assign-students label {
        display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: #d1d1d6;
        cursor: pointer;
      }
      .cgt-assign-students label small { color: #6e6e73; margin-left: 4px; font-size: 10.5px; }
      .cgt-assign-dialog-actions {
        display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px;
      }
      .cgt-assign-secondary {
        background: rgba(255,255,255,0.05); color: #a1a1a6;
        border: 0.5px solid rgba(255,255,255,0.08);
        padding: 8px 16px; border-radius: 8px; cursor: pointer;
        font-weight: 600; font-size: 12px; font-family: inherit;
      }
    `;
    document.head.appendChild(style);
  }
}

function esc(text: string): string {
  return text.replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c] ?? c));
}

export const assignmentsPanel = new AssignmentsPanel();
